import { API } from './axios';

// Create a separate axios instance for admin API calls
export const AdminAPI = {
  // Search applicants
  searchApplicants: async (searchTerm) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await API.get('/applicants', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          filters: {
            email: { $containsi: searchTerm },
          },
          populate: {
            user: true,
          },
        },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error searching applicants:', error);
      throw error;
    }
  },

  // Get all applicants with pagination
  getAllApplicants: async (page = 1, pageSize = 25) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await API.get('/users', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          pagination: {
            page,
            pageSize,
          },
          populate: '*',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching applicants:', error);
      throw error;
    }
  },

  // Get applicant by ID
  getApplicantById: async (applicantId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await API.get(`/applicants/${applicantId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          populate: {
            user: {
              populate: '*',
            },
            availability: {
              populate: '*',
            },
            mediaData: {
              populate: {
                driversLicense: {
                  populate: '*',
                },
                workspaceImages: {
                  populate: '*',
                },
                washingMachineLabel: {
                  populate: '*',
                },
                foldedItemsImages: {
                  populate: '*',
                },
                introVideo: {
                  populate: '*',
                },
              },
            },
            personalInfo: {
              populate: '*',
            },
            qualifications: {
              populate: '*',
            },
            experience: {
              populate: '*',
            },
            agreements: {
              populate: '*',
            },
          },
        },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching applicant:', error);
      throw error;
    }
  },

  // Get applicant record by user ID
  getApplicantByUserId: async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await API.get('/applicants', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          filters: {
            user: {
              id: userId,
            },
          },
          sort: ['createdAt:desc'], // Get most recent first
          pagination: {
            limit: 1, // Only need one record
          },
          populate: '*', // or populate: ['mediaData', 'user'] for specific fields
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching applicant by user ID:', error);
      throw error;
    }
  },

  // Update applicant status (updates the applicant record, not user)
  updateApplicantStatus: async (applicantDocumentId, applicationStatus) => {
    try {
      const token =
        localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await API.put(
        `/applicants/${applicantDocumentId}`,
        {
          data: {
            applicationStatus: applicationStatus,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating applicant status:', error);
      throw error;
    }
  },

  // Update user status (if needed separately)
  updateUserStatus: async (userId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await API.put(
        `/users/${userId}`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  },

  // Get applicant media files
  getApplicantMedia: async (applicantId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await API.get(`/applicant-media`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          filters: {
            applicant: applicantId,
          },
          populate: '*',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching applicant media:', error);
      throw error;
    }
  },

  // Delete media file
  deleteMediaFile: async (mediaId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await API.delete(`/applicant-media/${mediaId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting media file:', error);
      throw error;
    }
  },

  // Delete uploaded file from Strapi
  deleteUploadedFile: async (fileId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await API.delete(`/upload/files/${fileId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting uploaded file:', error);
      throw error;
    }
  },

  // Delete all media files for an applicant
  deleteAllApplicantMedia: async (applicantData) => {
    try {
      const token =
        localStorage.getItem('adminToken') || localStorage.getItem('token');
      const deletedFiles = [];
      const failedFiles = [];
      const mediaData = applicantData?.mediaData;

      if (!mediaData) {
        return { deletedFiles, failedFiles };
      }

      // Helper to extract file IDs from media items
      const extractFileIds = (mediaArray) => {
        if (!mediaArray) return [];
        const items = Array.isArray(mediaArray) ? mediaArray : [mediaArray];
        return items.map((item) => item?.id).filter((id) => id);
      };

      // Collect all file IDs
      const fileIds = [
        ...extractFileIds(mediaData.driversLicense),
        ...extractFileIds(mediaData.workspaceImages),
        ...extractFileIds(mediaData.washingMachineLabel),
        ...extractFileIds(mediaData.foldedItemsImages),
        ...extractFileIds(mediaData.introVideo),
      ];

      if (fileIds.length === 0) {
        return { deletedFiles, failedFiles };
      }

      // Delete each file from Strapi upload storage
      for (const fileId of fileIds) {
        try {
          await API.delete(`/upload/files/${fileId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          deletedFiles.push(fileId);
        } catch (error) {
          failedFiles.push({
            fileId,
            error: error.response?.data || error.message,
          });
          console.error(
            `Failed to delete file ${fileId}:`,
            error.response?.data || error.message
          );
        }
      }

      // Clear mediaData from applicant record
      if (deletedFiles.length > 0) {
        try {
          await API.put(
            `/applicants/${applicantData.documentId}`,
            {
              data: {
                mediaData: {
                  driversLicense: null,
                  workspaceImages: null,
                  washingMachineLabel: null,
                  foldedItemsImages: null,
                  introVideo: null,
                },
              },
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          console.log(
            `✅ Cleared mediaData for applicant ${applicantData.documentId}`
          );
        } catch (error) {
          console.error(
            'Failed to clear mediaData:',
            error.response?.data || error.message
          );
          // Add this failure to the response but don't throw
          failedFiles.push({
            operation: 'clear-mediaData',
            error: error.response?.data || error.message,
          });
        }
      }

      // Return results - let the caller decide how to handle failures
      return { deletedFiles, failedFiles };
    } catch (error) {
      console.error('Error deleting applicant media:', error);
      // Re-throw the error with the original message
      throw error;
    }
  },

  // Upload media files (for applicants during interview stage)
  uploadMedia: async (formData) => {
    try {
      const token = localStorage.getItem('token'); // Use applicant token
      const response = await API.post('/upload', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    }
  },

  // Link uploaded media to applicant
  linkMediaToApplicant: async (applicantId, mediaData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await API.post(
        '/applicant-media',
        {
          data: {
            applicant: applicantId,
            ...mediaData,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error linking media to applicant:', error);
      throw error;
    }
  },

  // Create mediaData entry with all uploaded files (as component in Applicant)
  createMediaData: async (applicantId, uploadedMediaIds) => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔄 Attempting to update applicant ID:', applicantId);
      console.log('🔄 API endpoint:', `/applicants/${applicantId}`);
      console.log('🔄 Payload:', {
        data: {
          mediaData: {
            driversLicense: uploadedMediaIds.driversLicense,
            workspaceImages: uploadedMediaIds.workspaceImages,
            washingMachineLabel: uploadedMediaIds.washingMachineLabel,
            foldedItemsImages: uploadedMediaIds.foldedItemsImages,
            introVideo: uploadedMediaIds.introVideo,
          },
        },
      });

      const response = await API.put(
        `/applicants/${applicantId}`,
        {
          data: {
            mediaData: {
              driversLicense: uploadedMediaIds.driversLicense,
              workspaceImages: uploadedMediaIds.workspaceImages,
              washingMachineLabel: uploadedMediaIds.washingMachineLabel,
              foldedItemsImages: uploadedMediaIds.foldedItemsImages,
              introVideo: uploadedMediaIds.introVideo,
            },
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log('✅ Update successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating media data:', error);
      console.error('❌ Full error response:', error.response?.data);
      console.error('❌ Status code:', error.response?.status);
      throw error;
    }
  },

  // Create a new applicant record
  createApplicant: async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await API.post(
        '/applicants',
        {
          data: {
            user: userId,
            mediaData: null,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating applicant record:', error);
      throw error;
    }
  },

  // Get signed URL for S3 file (if using presigned URLs)
  getSignedUrl: async (fileKey) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await API.get(`/media/signed-url`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          key: fileKey,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      throw error;
    }
  },
};

export default AdminAPI;
